import {
  APIAttachment,
  APIDocumentArchiveFolder,
  ArdoqId,
  isContentTypeSupportedByImagePreview,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { formatDateTimeShort } from '@ardoq/date-time';
import { IconName } from '@ardoq/icons';
import { DropdownButtonOptionType } from '@ardoq/dropdown-menu-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import { invariant } from '@ardoq/invariant';
import { Locale } from '@ardoq/locale';
import {
  AbsoluteScrollPosition,
  CellCoordinate,
  DragAndDroppableProps,
  EditableProps,
  Row,
  ScrollableProps,
  SortDescriptor,
} from '@ardoq/table';
import {
  ConfirmMoveToNewFolderPayload,
  DocumentArchiveFilters,
  FilterExpression,
  MoveAttachmentsPayload,
  MoveFolderPayload,
} from './actions';
import { getTableFilterBarLabels } from 'tableFilterBar/getTableFilterBarLabels';
import type { DocumentArchiveState } from './DocumentArchivePage';
import {
  ContextMenuItem,
  DeleteAttachmentProps,
  DeleteConfirmationModalProps,
  DeleteConfirmationSummaryProps,
  DeleteFolderProps,
  DocumentArchiveFloatingActionBarProps,
  DocumentArchiveViewProps,
  DocumentArchiveTableViewProps,
  DropZoneProps,
  GroupedEntity,
  GroupedEntityType,
  MoveToFolderOption,
  MoveToModalProps,
} from './DocumentArchivePageView';
import { getFloatingActionBarLabels } from 'locales/getTableLabels';
import {
  EntityAttachmentType,
  toAttachmentIcon,
  toAttachmentType,
} from './attachmentPreview';

const compareSizeInBytes = (
  a: GroupedEntity['size'] | null,
  b: GroupedEntity['size'] | null
): number => {
  // Handle null objects (shouldn't happen but for type safety)
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const aBytes = a.bytes;
  const bBytes = b.bytes;

  // Folders have null size, sort them to the end
  if (aBytes === null && bBytes === null) return 0;
  if (aBytes === null) return 1;
  if (bBytes === null) return -1;

  // Compare numeric byte values
  return aBytes - bBytes;
};

export type DocumentArchiveCommands = {
  dropZone: DropZoneCommands;
  deleteConfirmationModal: DeleteConfirmationModalCommands;
  documentArchiveTable: DocumentArchiveTableCommands;
  moveToModal: MoveToModalCommands;
};

type MoveToModalCommands = {
  openMoveToModal: (folderIds: string[], attachmentIds: string[]) => void;
  setMoveToModalDestination: (folderId: string | 'root') => void;
  setMoveToModalSearchTerm: (searchTerm: string) => void;
  setMoveToModalNewFolderName: (name: string | null) => void;
  confirmMoveToNewFolder: (payload: ConfirmMoveToNewFolderPayload) => void;
  closeMoveToModal: VoidFunction;
};

export const toDocumentArchivePageViewProps = (
  state: DocumentArchiveState,
  folders: APIDocumentArchiveFolder[],
  attachments: APIAttachment[],
  typeId: ArdoqId,
  locale: Locale,
  commands: DocumentArchiveCommands,
  i18nLocale: I18nLocale
): DocumentArchiveViewProps => {
  return {
    title: t(
      i18nLocale,
      'documentArchive:organizationDocumentsTitle',
      'Organization documents'
    ),
    table: toDocumentArchiveTableViewProps(
      state,
      attachments,
      folders,
      typeId,
      locale,
      commands.documentArchiveTable,
      i18nLocale
    ),
    dropZone: toDropZoneProps(
      state,
      typeId,
      attachments,
      commands.dropZone,
      i18nLocale
    ),
    deleteConfirmationModal: toDeleteConfirmationModalProps(
      state,
      folders,
      attachments,
      commands.deleteConfirmationModal,
      i18nLocale
    ),
    moveToModal: toMoveToModalProps(
      state,
      folders,
      attachments,
      typeId,
      commands.documentArchiveTable.dragAndDroppable,
      commands.moveToModal,
      i18nLocale
    ),
  };
};

type DropZoneCommands = {
  showDragOverlay: (show: boolean) => void;
  uploadFiles: (
    files: File[],
    typeId: ArdoqId,
    existingAttachmentNames: string[],
    dropped: boolean
  ) => void;
};

const toDropZoneProps = (
  state: DocumentArchiveState,
  typeId: ArdoqId,
  attachments: APIAttachment[],
  commands: DropZoneCommands,
  i18nLocale: I18nLocale
): DropZoneProps => ({
  hovered: state.isDragging,
  dragActive: state.isDragging,
  text: state.isDragging
    ? t(
        i18nLocale,
        'documentArchive:dropZone.dropToUpload',
        'Drop files to upload'
      )
    : t(
        i18nLocale,
        'documentArchive:dropZone.dragDropToUpload',
        'Drag and drop files here to upload'
      ),
  uploadFilesAriaLabel: t(
    i18nLocale,
    'documentArchive:dropZone.uploadFilesAriaLabel',
    'Upload files'
  ),
  onDragEnter: () => commands.showDragOverlay(true),
  onDragLeave: () => commands.showDragOverlay(false),
  onDrop: (files: File[]) =>
    commands.uploadFiles(
      files,
      typeId,
      attachments.map(a => a.filename),
      state.isDragging
    ),
});

type DeleteConfirmationModalCommands = {
  confirmDelete: (folderIds: string[], attachmentIds: string[]) => void;
  cancelDelete: () => void;
};

const toDeleteConfirmationModalProps = (
  state: DocumentArchiveState,
  folders: APIDocumentArchiveFolder[],
  attachments: APIAttachment[],
  commands: DeleteConfirmationModalCommands,
  i18nLocale: I18nLocale
): DeleteConfirmationModalProps => ({
  isOpen: state.deleteRequest.open,
  summary: toDeleteConfirmationSummary(
    state.deleteRequest,
    folders,
    attachments,
    i18nLocale
  ),
  headerText: t(
    i18nLocale,
    'documentArchive:deleteConfirmation.title',
    'Confirm deletion'
  ),
  cancelButtonText: t(
    i18nLocale,
    'documentArchive:deleteConfirmation.cancel',
    'Cancel'
  ),
  deleteButtonText: t(
    i18nLocale,
    'documentArchive:deleteConfirmation.delete',
    'Delete'
  ),
  onConfirm: () =>
    commands.confirmDelete(
      state.deleteRequest.folderIds,
      state.deleteRequest.attachmentIds
    ),
  onCancel: () => commands.cancelDelete(),
});

const toDeleteConfirmationSummary = (
  deleteRequest: DocumentArchiveState['deleteRequest'],
  folders: APIDocumentArchiveFolder[],
  attachments: APIAttachment[],
  i18nLocale: I18nLocale
): DeleteConfirmationSummaryProps => {
  const folderDeletionSummary = buildFolderDeletionSummary(
    deleteRequest.folderIds,
    folders,
    attachments
  );

  const attachmentDeletionSummary = buildAttachmentDeletionSummary(
    deleteRequest.attachmentIds,
    attachments
  );

  const folderCount = folderDeletionSummary.folderCount;
  const attachmentCount =
    folderDeletionSummary.attachmentCount +
    attachmentDeletionSummary.attachmentCount;

  return {
    folderCount,
    attachmentCount,
    rootFolders: folderDeletionSummary.rootFolders,
    rootAttachments: attachmentDeletionSummary.rootAttachments,
    summaryText: t(
      i18nLocale,
      'documentArchive:deleteConfirmation.summary',
      { folderCount, attachmentCount },
      `This will delete the following items ({{folderCount}} folders, {{attachmentCount}} attachments):`
    ),
  };
};

const buildFolderDeletionSummary = (
  folderIds: string[],
  folders: APIDocumentArchiveFolder[],
  attachments: APIAttachment[]
): {
  folderCount: number;
  attachmentCount: number;
  rootFolders: DeleteFolderProps[];
} => {
  const deletionResults = folderIds
    .map(folderId => folders.find(folder => folder._id === folderId))
    .filter(isTruthy)
    .map(folder => buildDeleteFolderProps(folder, folders, attachments));

  let folderCount = 0;
  let attachmentCount = 0;
  const rootFolders: DeleteFolderProps[] = [];

  for (const result of deletionResults) {
    rootFolders.push(result.folderProps);
    folderCount += result.foldersDeleted;
    attachmentCount += result.attachmentsDeleted;
  }

  return {
    folderCount,
    attachmentCount,
    rootFolders,
  };
};

const buildAttachmentDeletionSummary = (
  attachmentIds: string[],
  attachments: APIAttachment[]
): {
  attachmentCount: number;
  rootAttachments: DeleteAttachmentProps[];
} => {
  const rootAttachments = attachmentIds
    .map(attachmentId => attachments.find(a => a._id === attachmentId))
    .filter(isTruthy)
    .map(attachment => toDeleteAttachmentProps(attachment));

  return {
    attachmentCount: rootAttachments.length,
    rootAttachments,
  };
};

const toDeleteAttachmentProps = (
  attachment: APIAttachment
): DeleteAttachmentProps => ({
  type: 'attachment',
  name: attachment.filename,
});

const buildDeleteFolderProps = (
  folder: APIDocumentArchiveFolder,
  allFolders: APIDocumentArchiveFolder[],
  allAttachments: APIAttachment[]
): {
  folderProps: DeleteFolderProps;
  foldersDeleted: number;
  attachmentsDeleted: number;
} => {
  const attachmentChildren = buildAttachmentChildren(folder, allAttachments);
  const subfolderChildren = buildSubfolderChildren(
    folder,
    allFolders,
    allAttachments
  );

  const foldersDeleted = 1 + subfolderChildren.foldersDeleted;
  const attachmentsDeleted =
    attachmentChildren.length + subfolderChildren.attachmentsDeleted;

  return {
    folderProps: {
      type: 'folder',
      name: folder.name,
      children: [...attachmentChildren, ...subfolderChildren.folderProps],
    },
    foldersDeleted,
    attachmentsDeleted,
  };
};

const buildAttachmentChildren = (
  folder: APIDocumentArchiveFolder,
  allAttachments: APIAttachment[]
): DeleteAttachmentProps[] => {
  const attachmentsInFolder = allAttachments.filter(
    attachment => attachment.folder === folder._id
  );

  return attachmentsInFolder.map(attachment =>
    toDeleteAttachmentProps(attachment)
  );
};

const buildSubfolderChildren = (
  folder: APIDocumentArchiveFolder,
  allFolders: APIDocumentArchiveFolder[],
  allAttachments: APIAttachment[]
): {
  folderProps: DeleteFolderProps[];
  foldersDeleted: number;
  attachmentsDeleted: number;
} => {
  const subfolders = allFolders.filter(f => f.parent === folder._id);

  return subfolders.reduce<{
    folderProps: DeleteFolderProps[];
    foldersDeleted: number;
    attachmentsDeleted: number;
  }>(
    (acc, subfolder) => {
      const result = buildDeleteFolderProps(
        subfolder,
        allFolders,
        allAttachments
      );

      return {
        folderProps: [...acc.folderProps, result.folderProps],
        foldersDeleted: acc.foldersDeleted + result.foldersDeleted,
        attachmentsDeleted: acc.attachmentsDeleted + result.attachmentsDeleted,
      };
    },
    { folderProps: [], foldersDeleted: 0, attachmentsDeleted: 0 }
  );
};

const generateIncrementingFolderName = (
  existingFolders: APIDocumentArchiveFolder[]
): string => {
  const baseName = 'untitled folder';
  const folderNames = existingFolders.map(folder => folder.name.toLowerCase());

  // Check if base name exists
  if (!folderNames.includes(baseName)) {
    return baseName;
  }

  // Find all numbered variants
  const numberedFolders = folderNames
    .map(name => {
      const match = name.match(/^untitled folder (\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num): num is number => num !== null);

  // Find the next available number
  let nextNumber = 2;
  while (
    folderNames.includes(`${baseName} ${nextNumber}`) ||
    numberedFolders.includes(nextNumber)
  ) {
    nextNumber++;
  }

  return `${baseName} ${nextNumber}`;
};

type MoveToRowCommands = {
  openMoveToModal: (folderIds: string[], attachmentIds: string[]) => void;
};

type FolderMenuItemCommands = EditableCommands &
  DeleteRowsCommands &
  MoveToRowCommands;

export type DocumentArchiveBulkAction = Omit<ContextMenuItem, 'onClick'> & {
  onClick: VoidFunction;
};

export type DocumentArchiveBulkActions = {
  moveTo: DocumentArchiveBulkAction;
  deleteItem: DocumentArchiveBulkAction;
};

const toMoveToMenuItem = (
  folderIds: string[],
  attachmentIds: string[],
  commands: MoveToRowCommands,
  i18nLocale: I18nLocale
): DocumentArchiveBulkAction => {
  const totalSelected = folderIds.length + attachmentIds.length;
  const name =
    totalSelected > 1
      ? t(
          i18nLocale,
          'documentArchive:contextMenu.moveToWithCount',
          { count: totalSelected },
          'Move to ({{count}})'
        )
      : t(i18nLocale, 'documentArchive:contextMenu.moveTo', 'Move to');

  return {
    name,
    label: name,
    onClick: () => commands.openMoveToModal(folderIds, attachmentIds),
  };
};

const toFolderMenuItems = (
  folderId: string,
  commands: FolderMenuItemCommands,
  i18nLocale: I18nLocale
): ContextMenuItem[] => [
  toRenameRowMenuItem(folderId, commands, i18nLocale),
  toMoveToMenuItem([folderId], [], commands, i18nLocale),
  toDeleteMenuItem([], [folderId], commands, i18nLocale),
];

type AttachmentMenuItemCommands = EditableCommands &
  PreviewAttachmentCommand &
  DownloadAttachmentCommand &
  DeleteRowsCommands &
  MoveToRowCommands;

type PreviewAttachmentCommand = {
  showImagePreviewModal: (title: string, url: string) => void;
};

const toPreviewAttachmentMenuItem = (
  attachmentName: string,
  attachmentUri: string,
  commands: PreviewAttachmentCommand,
  i18nLocale: I18nLocale
): ContextMenuItem => {
  const label = t(i18nLocale, 'documentArchive:contextMenu.preview', 'Preview');
  return {
    name: label,
    label,
    onClick: () =>
      commands.showImagePreviewModal(attachmentName, attachmentUri),
  };
};

const toRenameRowMenuItem = (
  rowId: string,
  commands: EditableCommands,
  i18nLocale: I18nLocale
): ContextMenuItem => {
  const label = t(i18nLocale, 'documentArchive:contextMenu.rename', 'Rename');
  return {
    name: label,
    label,
    onClick: () => commands.setEditedCell({ rowId, columnId: '_group' }),
  };
};

type DownloadAttachmentCommand = {
  downloadAttachment: (uri: string) => void;
};

const toDownloadAttachmentMenuItem = (
  attachmentUri: string,
  commands: DownloadAttachmentCommand,
  i18nLocale: I18nLocale
): ContextMenuItem => {
  const label = t(
    i18nLocale,
    'documentArchive:contextMenu.download',
    'Download'
  );
  return {
    name: label,
    label,
    onClick: () => commands.downloadAttachment(attachmentUri),
  };
};

const toAttachmentMenuItems = (
  attachmentId: string,
  attachmentUri: string,
  attachmentName: string,
  isSupportedByImagePreview: boolean,
  commands: AttachmentMenuItemCommands,
  i18nLocale: I18nLocale
): ContextMenuItem[] => [
  ...(isSupportedByImagePreview
    ? [
        toPreviewAttachmentMenuItem(
          attachmentName,
          attachmentUri,
          commands,
          i18nLocale
        ),
      ]
    : []),
  toRenameRowMenuItem(attachmentId, commands, i18nLocale),
  toDownloadAttachmentMenuItem(attachmentUri, commands, i18nLocale),
  toMoveToMenuItem([], [attachmentId], commands, i18nLocale),
  toDeleteMenuItem([attachmentId], [], commands, i18nLocale),
];

type DeleteRowsCommands = {
  requestDeletion: (attachmentIds: string[], folderIds: string[]) => void;
};

const toDeleteMenuItem = (
  attachmentIds: string[],
  folderIds: string[],
  commands: DeleteRowsCommands,
  i18nLocale: I18nLocale
): DocumentArchiveBulkAction => {
  const totalSelected = attachmentIds.length + folderIds.length;

  const name =
    totalSelected > 1
      ? t(
          i18nLocale,
          'documentArchive:contextMenu.deleteSelectedWithCount',
          { count: totalSelected },
          'Delete selected ({{count}})'
        )
      : t(i18nLocale, 'documentArchive:contextMenu.delete', 'Delete');

  return {
    name,
    label: name,
    icon: { name: IconName.DELETE },
    onClick: () => commands.requestDeletion(attachmentIds, folderIds),
    color: 'textError',
    buttonType: DropdownButtonOptionType.DANGER,
  };
};

export const toBulkActionItems = (
  folderIds: string[],
  attachmentIds: string[],
  commands: AttachmentMenuItemCommands,
  i18nLocale: I18nLocale
): DocumentArchiveBulkActions => ({
  moveTo: toMoveToMenuItem(folderIds, attachmentIds, commands, i18nLocale),
  deleteItem: toDeleteMenuItem(attachmentIds, folderIds, commands, i18nLocale),
});

const toBulkActionList = (
  folderIds: string[],
  attachmentIds: string[],
  commands: AttachmentMenuItemCommands,
  i18nLocale: I18nLocale
): DocumentArchiveBulkAction[] => {
  const { moveTo, deleteItem } = toBulkActionItems(
    folderIds,
    attachmentIds,
    commands,
    i18nLocale
  );
  return [moveTo, deleteItem];
};

type DocumentArchiveTableCommands = {
  setSearchTerm: (value: string) => void;
  setFilterExpressions: (expressions: FilterExpression[]) => void;
  setFolderExpanded: (id: string) => void;
  setFolderClosed: (id: string) => void;
  renameFolder: (
    id: string,
    newName: string,
    typeId: ArdoqId,
    version: number
  ) => void;
  renameAttachment: (
    id: string,
    newName: string,
    typeId: ArdoqId,
    version: number
  ) => void;
  rejectAttachmentRename: VoidFunction;
  setSelectedIds: (ids: string[]) => void;
  setSortDescriptor: (descriptor: SortDescriptor | null) => void;
  createFolder: (name: string, typeId: ArdoqId) => void;

  attachmentMenuItem: AttachmentMenuItemCommands;
  folderMenuItem: FolderMenuItemCommands;
  dragAndDroppable: DragAndDroppableCommands;
  scrollable: ScrollableCommands;
};

const toFloatingActionBarProps = (
  state: DocumentArchiveState,
  attachments: APIAttachment[],
  folders: APIDocumentArchiveFolder[],
  commands: DocumentArchiveTableCommands,
  i18nLocale: I18nLocale
): DocumentArchiveFloatingActionBarProps | null => {
  const folderIds = state.selectedIds.filter(id =>
    folders.some(folder => folder._id === id)
  );
  const attachmentIds = state.selectedIds.filter(id =>
    attachments.some(attachment => attachment._id === id)
  );
  const selectedCount = folderIds.length + attachmentIds.length;

  if (selectedCount === 0) {
    return null;
  }

  return {
    selectedCount,
    entityName:
      selectedCount > 1
        ? t(i18nLocale, 'documentArchive:selection.entityNamePlural', 'items')
        : t(i18nLocale, 'documentArchive:selection.entityNameSingular', 'item'),
    actions: toBulkActionList(
      folderIds,
      attachmentIds,
      commands.attachmentMenuItem,
      i18nLocale
    ),
    onCancel: () => commands.setSelectedIds([]),
    labels: getFloatingActionBarLabels(i18nLocale),
  };
};

const toDocumentArchiveTableViewProps = (
  state: DocumentArchiveState,
  attachments: APIAttachment[],
  folders: APIDocumentArchiveFolder[],
  typeId: ArdoqId,
  locale: Locale,
  commands: DocumentArchiveTableCommands,
  i18nLocale: I18nLocale
): DocumentArchiveTableViewProps => {
  const filteredAttachments = attachments.filter(attachment =>
    isAttachmentIncludedByFilterExpressions(attachment, state.filterExpressions)
  );
  const filteredFolders =
    state.filterExpressions.length < 1
      ? folders
      : folders.filter(
          folder =>
            filteredAttachments.some(attachment =>
              attachmentInFolder(attachment, folder, folders)
            ) || folderMatchesFilterExpressions(folder, state.filterExpressions)
        );

  // Build folder hierarchy - create a map for quick lookup
  const folderMap = new Map<string, APIDocumentArchiveFolder>();
  filteredFolders.forEach(folder => folderMap.set(folder._id, folder));

  // Helper function to build folder path

  const folderRows = filteredFolders.map(folder =>
    toFolderRow(
      folder,
      state,
      folderMap,
      attachments,
      locale,
      commands,
      i18nLocale
    )
  );
  const attachmentRows = filteredAttachments.map(attachment =>
    toAttachmentRow(
      attachments,
      attachment,
      folderMap,
      locale,
      commands,
      i18nLocale
    )
  );

  const allRows = [...folderRows, ...attachmentRows];
  const rows = filterRowsBySearchTerm(allRows, state.searchTerm);

  return {
    rows,
    search: {
      value: state.searchTerm,
      onValueChange: (value: string) => commands.setSearchTerm(value),
    },
    filter: {
      filters: getAttachmentFilters(i18nLocale),
      optionCounts: toOptionsCount(attachments),
      filterExpressions: state.filterExpressions,
      onFilterExpressionChange: filterExpressions =>
        commands.setFilterExpressions(filterExpressions),
      labels: getTableFilterBarLabels(i18nLocale, locale),
    },
    columnTitles: {
      fileName: t(
        i18nLocale,
        'documentArchive:columnTitle.fileName',
        'File name'
      ),
      size: t(i18nLocale, 'documentArchive:columnTitle.size', 'Size'),
      lastModified: t(
        i18nLocale,
        'documentArchive:columnTitle.lastModified',
        'Last updated'
      ),
      modifiedBy: t(
        i18nLocale,
        'documentArchive:columnTitle.modifiedBy',
        'Updated by'
      ),
    },
    searchPlaceholder: t(
      i18nLocale,
      'documentArchive:searchPlaceholder',
      'Search all columns...'
    ),
    createFolderLabel: t(
      i18nLocale,
      'documentArchive:createFolderButton',
      'Create new folder'
    ),
    grouping: {
      expandedIds: state.searchTerm
        ? rows.map(row => row.id)
        : state.expandedIds,
      expandFolder: (id: string) => commands.setFolderExpanded(id),
      closeFolder: (id: string) => commands.setFolderClosed(id),
      renameRow: (id: string, newName: string) => {
        const trimmedName = newName.trim();
        const folder = folders.find(f => f._id === id);

        if (folder) {
          if (trimmedName === '' || folder.name === trimmedName) {
            return;
          }

          return commands.renameFolder(
            id,
            trimmedName,
            typeId,
            folder._version
          );
        }

        const attachment = attachments.find(d => d._id === id);
        if (
          trimmedName === '' ||
          !attachment ||
          attachment.filename === trimmedName
        ) {
          return;
        }

        const isDuplicateName = attachments.some(
          a => a.filename === trimmedName
        );

        if (isDuplicateName) {
          return commands.rejectAttachmentRename();
        }

        return commands.renameAttachment(
          id,
          trimmedName,
          typeId,
          attachment._version
        );
      },
    },
    dragAndDroppable: toDragAndDroppableProps(
      attachments,
      filteredFolders,
      typeId,
      state.selectedIds,
      commands.dragAndDroppable,
      i18nLocale
    ),
    selectable: {
      mode: 'multiple',
      ids: state.selectedIds,
      onChange: selectedIds => commands.setSelectedIds(selectedIds),
    },
    sortable: {
      descriptor: state.sortDescriptor,
      onChange: descriptor => commands.setSortDescriptor(descriptor),
    },
    editable: toEditableProps(state.editedCell, commands.attachmentMenuItem),
    scrollable: toScrollableProps(state, commands.scrollable),
    highlightable: { rowIds: state.highlightedRows },
    floatingActionBar: toFloatingActionBarProps(
      state,
      attachments,
      folders,
      commands,
      i18nLocale
    ),
    tablePrimaryActionButton: null,
    createFolder: {
      onClick: () =>
        commands.createFolder(
          generateIncrementingFolderName(filteredFolders),
          typeId
        ),
    },
    compareSizeInBytes,
  };
};

type SingleSelectFolderContext = {
  isMultiSelect: false;
  type: 'folder';
};

type SingleSelectAttachmentContext = {
  isMultiSelect: false;
  type: 'attachment';
};

type SingleSelectionContext =
  | SingleSelectFolderContext
  | SingleSelectAttachmentContext;

type MultiSelectionContext = {
  isMultiSelect: true;
  selectedFolders: GroupedEntity[];
  selectedAttachments: GroupedEntity[];
};

type SelectionContext = MultiSelectionContext | SingleSelectionContext;

const toSelectionContext = (
  rowData: GroupedEntity,
  selectedRows: GroupedEntity[]
): SelectionContext => {
  const isSelected = selectedRows.some(row => row.id === rowData.id);

  if (!isSelected) {
    return {
      isMultiSelect: false,
      type: rowData.type === 'folder' ? 'folder' : 'attachment',
    };
  }

  const selectedFolders = selectedRows.filter(row => row.type === 'folder');
  const selectedAttachments = selectedRows.filter(row => row.type !== 'folder');

  return {
    isMultiSelect: true,
    selectedFolders,
    selectedAttachments,
  };
};

export const getGlobalContextMenuItems =
  (
    attachments: APIAttachment[],
    commands: DocumentArchiveTableCommands,
    i18nLocale: I18nLocale
  ) =>
  (rowData: GroupedEntity, selectedRows: GroupedEntity[]) => {
    const selectionContext = toSelectionContext(rowData, selectedRows);

    if (selectionContext.isMultiSelect) {
      const folderIds = selectionContext.selectedFolders.map(f => f.id);
      const attachmentIds = selectionContext.selectedAttachments.map(a => a.id);

      return toBulkActionList(
        folderIds,
        attachmentIds,
        commands.attachmentMenuItem,
        i18nLocale
      );
    }

    if (selectionContext.type === 'folder') {
      return toFolderMenuItems(rowData.id, commands.folderMenuItem, i18nLocale);
    }

    const allSelectedAttachments = selectedRows.filter(attachment =>
      isAttachmentType(attachment.type)
    );

    const attachmentUri =
      allSelectedAttachments.find(attachment => attachment.id === rowData.id)
        ?.uri ?? rowData.uri;

    if (!attachmentUri) {
      return [];
    }

    const attachment = attachments.find(a => a._id === rowData.id);

    invariant(
      attachment,
      `Attachment with id ${rowData.id} not found in allAttachments`
    );

    return toAttachmentMenuItems(
      rowData.id,
      attachmentUri,
      rowData._group.name,
      isContentTypeSupportedByImagePreview(attachment),
      commands.attachmentMenuItem,
      i18nLocale
    );
  };

const isAttachmentType = (
  type: GroupedEntityType
): type is EntityAttachmentType => {
  return type === 'image' || type === 'document' || type === 'misc';
};

const toFolderRow = (
  folder: APIDocumentArchiveFolder,
  state: DocumentArchiveState,
  folderMap: Map<string, APIDocumentArchiveFolder>,
  attachments: APIAttachment[],
  locale: Locale,
  commands: DocumentArchiveTableCommands,
  i18nLocale: I18nLocale
): Row<GroupedEntity> => {
  const folderPath = buildFolderPath(folder._id, folderMap);
  const isExpanded = state.expandedIds.includes(folder._id);

  return {
    id: folder._id,
    path: folderPath,
    allowDrop: true,
    data: {
      id: folder._id,
      type: 'folder',
      actions: {
        items: toFolderMenuItems(
          folder._id,
          commands.folderMenuItem,
          i18nLocale
        ),
      },
      _group: {
        name: folder.name,
        isFolder: true,
        isExpanded,
        icon: isExpanded ? IconName.OPENED_FOLDER : IconName.FOLDER,
        thumbnailUrl: null,
      },
      size: {
        displayValue: null,
        bytes: null,
      },
      lastModified: {
        displayValue: formatDateTimeShort(folder.lastUpdated, locale),
        timestamp: folder.lastUpdated ?? null,
      },
      modifiedBy: folder.lastModifiedByName,
      uri: null,
    },
    getContextMenuItems: getGlobalContextMenuItems(
      attachments,
      commands,
      i18nLocale
    ),
  };
};

const toAttachmentRow = (
  attachments: APIAttachment[],
  attachment: APIAttachment,
  folderMap: Map<string, APIDocumentArchiveFolder>,
  locale: Locale,
  commands: DocumentArchiveTableCommands,
  i18nLocale: I18nLocale
): Row<GroupedEntity> => {
  let attachmentPath: string[];

  if (attachment.folder) {
    const folderPath = buildFolderPath(attachment.folder, folderMap);
    attachmentPath = [...folderPath, attachment._id];
  } else {
    attachmentPath = [attachment._id];
  }

  const attachmentType = toAttachmentType(attachment['content-type']);
  const isSupportedByImagePreview =
    isContentTypeSupportedByImagePreview(attachment);

  return {
    id: attachment._id,
    path: attachmentPath,
    data: {
      id: attachment._id,
      type: attachmentType,
      actions: {
        items: toAttachmentMenuItems(
          attachment._id,
          attachment.uri,
          attachment.filename,
          isSupportedByImagePreview,
          commands.attachmentMenuItem,
          i18nLocale
        ),
      },
      _group: {
        name: attachment.filename,
        isFolder: false,
        isExpanded: false,
        icon: toAttachmentIcon(toAttachmentType(attachment['content-type'])),
        thumbnailUrl: attachmentType === 'image' ? attachment.uri : null,
      },
      size: {
        displayValue: toAttachmentSizeString(attachment.size),
        bytes: attachment.size,
      },
      lastModified: {
        displayValue: formatDateTimeShort(attachment.lastUpdated, locale),
        timestamp: attachment.lastUpdated ?? null,
      },
      modifiedBy: attachment.lastModifiedByName,
      uri: attachment.uri,
    },
    getContextMenuItems: getGlobalContextMenuItems(
      attachments,
      commands,
      i18nLocale
    ),
  };
};

type DragAndDroppableCommands = {
  moveFolder: (payload: MoveFolderPayload) => void;
  moveAttachments: (payload: MoveAttachmentsPayload) => void;
};

const toDragAndDroppableProps = (
  attachments: APIAttachment[],
  folders: APIDocumentArchiveFolder[],
  typeId: ArdoqId,
  selectedIds: string[],
  commands: DragAndDroppableCommands,
  i18nLocale: I18nLocale
): DragAndDroppableProps<GroupedEntity> => ({
  internalDrop: {
    onDrop: (sourceId, targetId) => {
      const moveFolderPayload = toMoveFolderPayload(
        sourceId,
        targetId,
        folders,
        typeId
      );

      if (moveFolderPayload) {
        return commands.moveFolder(moveFolderPayload);
      }

      const moveAttachmentsPayload = toMoveAttachmentsPayload(
        sourceId,
        targetId,
        attachments,
        folders,
        selectedIds
      );

      if (moveAttachmentsPayload) {
        return commands.moveAttachments(moveAttachmentsPayload);
      }
    },
  },
  rowDragText: rowData => {
    const types = draggedRowTypes(rowData);
    const movingSingle = (name: string) =>
      t(
        i18nLocale,
        'documentArchive:dragText.movingSingle',
        { name },
        'Moving {{name}}'
      );

    if (types.size === 1) {
      const onlyType = types.values().next().value;
      if (onlyType === 'folder') {
        return rowData.length > 1
          ? t(
              i18nLocale,
              'documentArchive:dragText.movingFolders',
              { count: rowData.length },
              'Moving {{count}} folders'
            )
          : movingSingle(rowData[0]._group.name);
      }
      return rowData.length > 1
        ? t(
            i18nLocale,
            'documentArchive:dragText.movingAttachments',
            { count: rowData.length },
            'Moving {{count}} attachments'
          )
        : movingSingle(rowData[0]._group.name);
    }

    return rowData.length > 1
      ? t(
          i18nLocale,
          'documentArchive:dragText.movingItems',
          { count: rowData.length },
          'Moving {{count}} items'
        )
      : movingSingle(rowData[0]._group.name);
  },
});

const draggedRowTypes = (rowData: GroupedEntity[]) => {
  const types = new Set<GroupedEntityType>();
  rowData.forEach(row => types.add(row.type));

  return types;
};

type EditableCommands = {
  setEditedCell: (coordinate: CellCoordinate | null) => void;
};

const toEditableProps = (
  editedCell: CellCoordinate | null,
  commands: EditableCommands
): EditableProps => ({
  coordinate: editedCell,
  onSync: coordinate => commands.setEditedCell(coordinate),
});

type ScrollableCommands = {
  setScrollPosition: (scrollPosition: AbsoluteScrollPosition) => void;
};

const toScrollableProps = (
  state: DocumentArchiveState,
  commands: ScrollableCommands
): ScrollableProps => ({
  scrollPosition: state.scrollPosition,
  onSync: scrollPosition => commands.setScrollPosition(scrollPosition),
});

const toMoveFolderPayload = (
  sourceId: string,
  targetId: string | null,
  folders: APIDocumentArchiveFolder[],
  typeId: ArdoqId
): MoveFolderPayload | null => {
  const movedFolder = folders.find(f => f._id === sourceId);

  if (!movedFolder) {
    return null;
  }

  const targetFolder = folders.find(f => f._id === targetId);
  const newParentId = targetFolder ? targetFolder._id : null;
  if (movedFolder.parent === newParentId) {
    // No change in parent
    return null;
  }

  return {
    typeId,
    foldersWithNewParent: [{ ...movedFolder, parent: newParentId }],
  };
};

const toMoveAttachmentsPayload = (
  sourceId: string,
  targetId: string | null,
  attachments: APIAttachment[],
  folders: APIDocumentArchiveFolder[],
  selectedIds: string[]
): MoveAttachmentsPayload | null => {
  const movedAttachments = attachments.find(doc => doc._id === sourceId);
  if (!movedAttachments) {
    // Document not found
    return null;
  }

  const movedSelectedAttachments = selectedIds.includes(sourceId)
    ? attachments.filter(doc => selectedIds.includes(doc._id))
    : [movedAttachments];

  const targetFolder = folders.find(f => f._id === targetId);
  const newFolderId = targetFolder ? targetFolder._id : null;
  const movedToNewFolder = movedSelectedAttachments.filter(
    doc => doc.folder !== newFolderId
  );

  if (movedToNewFolder.length === 0) {
    // No change in folder
    return null;
  }

  return {
    attachmentIds: movedToNewFolder.map(doc => doc._id),
    folder: newFolderId,
  };
};

export const toAttachmentSizeString = (sizeInBytes: number): string => {
  const KB = 1000;
  const MB = KB * 1000;

  if (sizeInBytes < KB) {
    return `${sizeInBytes} B`;
  }

  if (sizeInBytes < MB) {
    return `${(sizeInBytes / KB).toFixed(2)} KB`;
  }

  return `${(sizeInBytes / MB).toFixed(2)} MB`;
};

const buildFolderPath = (
  folderId: string,
  folderMap: Map<string, APIDocumentArchiveFolder>
): string[] => {
  const path: string[] = [];
  let currentFolder = folderMap.get(folderId);

  // Build path from current folder to root
  const visited = new Set<string>();
  while (currentFolder && !visited.has(currentFolder._id)) {
    visited.add(currentFolder._id);
    path.unshift(currentFolder._id);
    if (currentFolder.parent) {
      currentFolder = folderMap.get(currentFolder.parent);
    } else {
      break;
    }
  }

  return path;
};

const getAttachmentFilters = (
  i18nLocale: I18nLocale
): DocumentArchiveFilters => [
  {
    id: 'filename',
    label: t(i18nLocale, 'documentArchive:filter.fileName', 'File name'),
    type: 'string',
    icon: {
      name: IconName.FORMAT_SIZE,
    },
  },
  {
    id: 'attachmentType',
    label: t(
      i18nLocale,
      'documentArchive:filter.attachmentType',
      'Attachment type'
    ),
    type: 'list',
    options: [
      {
        id: 'image',
        label: t(
          i18nLocale,
          'documentArchive:filter.attachmentTypeOption.image',
          'Image'
        ),
      },
      {
        id: 'document',
        label: t(
          i18nLocale,
          'documentArchive:filter.attachmentTypeOption.document',
          'Document'
        ),
      },
      {
        id: 'misc',
        label: t(
          i18nLocale,
          'documentArchive:filter.attachmentTypeOption.other',
          'Other'
        ),
      },
    ],
  },
];

const toOptionsCount = (
  attachments: APIAttachment[]
): Record<string, Record<string, number | undefined> | undefined> => ({
  attachmentType: attachments.reduce(
    (acc: Record<string, number | undefined>, attachment) => {
      const type = toAttachmentType(attachment['content-type']);
      return {
        ...acc,
        [type]: (acc[type] ?? 0) + 1,
      };
    },
    {}
  ),
});

const isAttachmentIncludedByFilterExpressions = (
  attachment: APIAttachment,
  filterExpressions: FilterExpression[]
): boolean => {
  return filterExpressions.every(expression => {
    const matched = doesAttachmentMatchFilterExpression(attachment, expression);

    switch (expression.operator) {
      case 'contains':
      case 'equal':
        return matched;
      case 'not_contains':
      case 'not_equal':
        return !matched;
      default:
        return true;
    }
  });
};

const doesFolderMatchFilterExpression = (
  folder: APIDocumentArchiveFolder,
  expression: FilterExpression
): boolean => {
  switch (expression.filterId) {
    case 'filename': {
      const folderName = folder.name.toLowerCase();
      const searchTerm = expression.selection.toLowerCase();

      if (
        expression.operator === 'equal' ||
        expression.operator === 'not_equal'
      ) {
        return folderName === searchTerm;
      }

      return folderName.includes(searchTerm);
    }
    default:
      return false;
  }
};

const folderMatchesFilterExpressions = (
  folder: APIDocumentArchiveFolder,
  filterExpressions: FilterExpression[]
): boolean => {
  return filterExpressions.every(expression => {
    const matched = doesFolderMatchFilterExpression(folder, expression);

    switch (expression.operator) {
      case 'contains':
      case 'equal':
        return matched;
      case 'not_contains':
      case 'not_equal':
        return !matched;
      default:
        return true;
    }
  });
};

const doesAttachmentMatchFilterExpression = (
  attachment: APIAttachment,
  expression: FilterExpression
): boolean => {
  switch (expression.filterId) {
    case 'filename': {
      const filename = attachment.filename.toLowerCase();
      const searchTerm = expression.selection.toLowerCase();

      if (
        expression.operator === 'equal' ||
        expression.operator === 'not_equal'
      ) {
        return filename === searchTerm;
      }

      return filename.includes(searchTerm);
    }
    case 'attachmentType': {
      const type = toAttachmentType(attachment['content-type']);
      return expression.selection.some(id => id === type);
    }
    default:
      return false;
  }
};

const attachmentInFolder = (
  attachment: APIAttachment,
  initialFolder: APIDocumentArchiveFolder,
  allFolders: APIDocumentArchiveFolder[]
): boolean => {
  let currentFolderId = attachment.folder;
  while (currentFolderId) {
    if (currentFolderId === initialFolder._id) {
      return true;
    }
    const currentFolder = allFolders.find(f => f._id === currentFolderId);
    currentFolderId = currentFolder?.parent || null;
  }
  return false;
};

const doesRowMatchSearchTerm = (
  row: Row<GroupedEntity>,
  searchTerm: string
): boolean => {
  const lowerSearchTerm = searchTerm.toLowerCase();

  const searchableValues = [
    row.data._group.name,
    row.data.size.displayValue,
    row.data.lastModified.displayValue,
    row.data.modifiedBy,
  ];

  return searchableValues.some(
    value => value && value.toLowerCase().includes(lowerSearchTerm)
  );
};

const filterRowsBySearchTerm = (
  rows: Row<GroupedEntity>[],
  searchTerm: string
): Row<GroupedEntity>[] => {
  if (!searchTerm) {
    return rows;
  }

  const matchingRows = rows.filter(row =>
    doesRowMatchSearchTerm(row, searchTerm)
  );

  const visibleIds = new Set<string>();

  for (const row of matchingRows) {
    if (row.path) {
      for (const id of row.path) {
        visibleIds.add(id);
      }
    }
  }

  return rows.filter(row => visibleIds.has(row.id));
};

const getDescendantFolderIds = (
  folderIds: string[],
  allFolders: APIDocumentArchiveFolder[]
): string[] => {
  const result: string[] = [];
  const queue = [...folderIds];
  const visited = new Set<string>(folderIds);

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const children = allFolders.filter(f => f.parent === currentId);
    for (const child of children) {
      if (!visited.has(child._id)) {
        visited.add(child._id);
        result.push(child._id);
        queue.push(child._id);
      }
    }
  }

  return result;
};

const toOrderedFolderOptions = (
  allFolders: APIDocumentArchiveFolder[],
  disabledIds: Set<string>,
  moveToModalCommands: MoveToModalCommands
): MoveToFolderOption[] => {
  const options: MoveToFolderOption[] = [];

  const buildOptions = (parentId: string | null, depth: number): void => {
    const children = allFolders.filter(f => f.parent === parentId);
    // Depth-first traversal to ensure correct ordering in the dropdown
    for (const folder of children) {
      options.push({
        id: folder._id,
        name: folder.name,
        depth,
        isDisabled: disabledIds.has(folder._id),
        onClick: () =>
          moveToModalCommands.setMoveToModalDestination(folder._id),
      });
      buildOptions(folder._id, depth + 1);
    }
  };

  buildOptions(null, 0);
  return options;
};

const filterFolderOptionsBySearchTerm = (
  options: MoveToFolderOption[],
  searchTerm: string
): MoveToFolderOption[] => {
  if (!searchTerm) {
    return options;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  return options.filter(
    option =>
      option.id === 'root' ||
      option.name.toLowerCase().includes(lowerSearchTerm)
  );
};

const toMoveToModalProps = (
  state: DocumentArchiveState,
  folders: APIDocumentArchiveFolder[],
  attachments: APIAttachment[],
  typeId: ArdoqId,
  dragAndDroppableCommands: DragAndDroppableCommands,
  moveToModalCommands: MoveToModalCommands,
  i18nLocale: I18nLocale
): MoveToModalProps => {
  const descendantIds = getDescendantFolderIds(
    state.moveToModal.folderIds,
    folders
  );
  const disabledIds = new Set<string>([
    ...state.moveToModal.folderIds,
    ...descendantIds,
  ]);

  const rootOption: MoveToFolderOption = {
    id: 'root',
    name: t(
      i18nLocale,
      'documentArchive:moveToModal.topLevel',
      'Top level (no folder)'
    ),
    depth: 0,
    isDisabled: false,
    onClick: () => moveToModalCommands.setMoveToModalDestination('root'),
  };

  const allFolderOptions = [
    rootOption,
    ...toOrderedFolderOptions(folders, disabledIds, moveToModalCommands),
  ];

  const folderOptions = filterFolderOptionsBySearchTerm(
    allFolderOptions,
    state.moveToModal.searchTerm
  );

  const isCreatingNewFolder = state.moveToModal.newFolderName !== null;
  const trimmedNewFolderName = state.moveToModal.newFolderName?.trim() ?? '';
  const isConfirmDisabled = isCreatingNewFolder && trimmedNewFolderName === '';

  return {
    isOpen: state.moveToModal.open,
    folderOptions,
    headerText: t(
      i18nLocale,
      'documentArchive:moveToModal.title',
      'Move to folder'
    ),
    cancelButtonText: t(
      i18nLocale,
      'documentArchive:moveToModal.cancel',
      'Cancel'
    ),
    moveButtonText: t(i18nLocale, 'documentArchive:moveToModal.move', 'Move'),
    createAndMoveButtonText: t(
      i18nLocale,
      'documentArchive:moveToModal.createAndMove',
      'Create and move'
    ),
    searchPlaceholder: t(
      i18nLocale,
      'documentArchive:moveToModal.searchPlaceholder',
      'Search folders...'
    ),
    folderDisabledTitle: t(
      i18nLocale,
      'documentArchive:moveToModal.folderDisabledTitle',
      'Cannot move a folder into itself or one of its subfolders.'
    ),
    createNewFolderButtonText: t(
      i18nLocale,
      'documentArchive:moveToModal.createNewFolderButton',
      'Create new folder'
    ),
    newFolderNamePlaceholder: t(
      i18nLocale,
      'documentArchive:moveToModal.newFolderNamePlaceholder',
      'Folder name'
    ),
    selectedDestinationFolderId: state.moveToModal.selectedDestinationFolderId,
    search: {
      value: state.moveToModal.searchTerm,
      onValueChange: (value: string) =>
        moveToModalCommands.setMoveToModalSearchTerm(value),
    },
    newFolderInput: {
      onStartEditing: () => moveToModalCommands.setMoveToModalNewFolderName(''),
      editing: isCreatingNewFolder
        ? {
            name: state.moveToModal.newFolderName ?? '',
            onNameChange: (name: string) =>
              moveToModalCommands.setMoveToModalNewFolderName(name),
            onCancel: () =>
              moveToModalCommands.setMoveToModalNewFolderName(null),
          }
        : null,
    },
    isConfirmDisabled,
    onConfirm: () => {
      if (isCreatingNewFolder) {
        if (trimmedNewFolderName === '') {
          return;
        }

        return moveToModalCommands.confirmMoveToNewFolder({
          newFolderName: trimmedNewFolderName,
          typeId,
          foldersToMove: folders.filter(f =>
            state.moveToModal.folderIds.includes(f._id)
          ),
          attachmentsToMove: attachments.filter(a =>
            state.moveToModal.attachmentIds.includes(a._id)
          ),
        });
      }

      const destinationFolderId =
        state.moveToModal.selectedDestinationFolderId === 'root'
          ? null
          : state.moveToModal.selectedDestinationFolderId;

      const foldersToMove = folders.filter(
        f =>
          state.moveToModal.folderIds.includes(f._id) &&
          f.parent !== destinationFolderId
      );

      if (foldersToMove.length > 0) {
        dragAndDroppableCommands.moveFolder({
          typeId,
          foldersWithNewParent: foldersToMove.map(f => ({
            ...f,
            parent: destinationFolderId,
          })),
        });
      }

      const attachmentsToMove = attachments.filter(
        a =>
          state.moveToModal.attachmentIds.includes(a._id) &&
          a.folder !== destinationFolderId
      );

      if (attachmentsToMove.length > 0) {
        dragAndDroppableCommands.moveAttachments({
          attachmentIds: attachmentsToMove.map(a => a._id),
          folder: destinationFolderId,
        });
      }

      moveToModalCommands.closeMoveToModal();
    },
    onCancel: () => moveToModalCommands.closeMoveToModal(),
  };
};
